'use client';

import { useMemo } from 'react';
import { useTranslations } from 'next-intl';
import { Box, VStack } from '@terra/ui-styled-system';
import { Link02Icon } from '@terra/ui-icons';
import type { ColDef, ICellRendererParams } from 'ag-grid-community';
import { EndpointMethodBadge } from '../EndpointMethodBadge';
import {
  getEndpointAssetTitle,
  getEndpointMethodLabel,
} from '../../../utils/endpoint-asset-display.utils';
import { Button } from '../../../components/button';
import { Spinner } from '../../../components/spinner';
import { Table } from '../../../components/table';
import { MetaDefault, MonoDefault } from '../../../components/typography';
import { AssetTabDrawerEmptyState } from './AssetTabDrawerEmptyState';
import {
  useWebpageEndpoints,
  type WebpageRelatedEndpoint,
} from '../hooks/useWebpageEndpoints';
import { ASSET_TYPE_VALUES } from '../types';

type WebpagesEndpointsTabProps = {
  webpageId: string;
  onOpenRelatedAsset?: (asset: {
    id: string;
    type: typeof ASSET_TYPE_VALUES.ENDPOINT;
    title: string;
  }) => void;
};

export function WebpagesEndpointsTab({
  webpageId,
  onOpenRelatedAsset,
}: WebpagesEndpointsTabProps) {
  const t = useTranslations('designSystem.applicationAssets.drawer.endpoints');
  const {
    endpoints,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
  } = useWebpageEndpoints({
    webpageId,
    enabled: true,
  });
  const columnDefs = useMemo<ColDef<WebpageRelatedEndpoint>[]>(
    () => [
      {
        field: 'request.method',
        headerName: t('columnMethod'),
        width: 110,
        sortable: true,
        cellRenderer: (params: ICellRendererParams<WebpageRelatedEndpoint>) => {
          if (!params.data) {
            return null;
          }

          return (
            <EndpointMethodBadge method={getEndpointMethodLabel(params.data)} />
          );
        },
      },
      {
        field: 'path',
        headerName: t('columnPath'),
        flex: 2,
        sortable: true,
        cellRenderer: (params: ICellRendererParams<WebpageRelatedEndpoint>) => (
          <MonoDefault truncate>
            {params.data ? getEndpointAssetTitle(params.data) : ''}
          </MonoDefault>
        ),
      },
      {
        field: 'description',
        headerName: t('columnDescription'),
        flex: 2,
        sortable: false,
        cellRenderer: (params: ICellRendererParams<WebpageRelatedEndpoint>) => (
          <MetaDefault color="fg.muted" truncate>
            {params.data?.description || '—'}
          </MetaDefault>
        ),
      },
    ],
    [t],
  );

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" py={10}>
        <Spinner />
      </Box>
    );
  }

  if (endpoints.length === 0) {
    return (
      <AssetTabDrawerEmptyState
        icon={Link02Icon}
        title={t('emptyTitle')}
        description={t('emptyDescription')}
      />
    );
  }

  return (
    <VStack gap={4} alignItems="stretch">
      <Table
        rowData={endpoints}
        columnDefs={columnDefs}
        getRowId={(params) => params.data.id}
        onRowClicked={(event) => {
          if (!event.data?.id || !onOpenRelatedAsset) {
            return;
          }

          onOpenRelatedAsset({
            id: event.data.id,
            type: ASSET_TYPE_VALUES.ENDPOINT,
            title: `${getEndpointMethodLabel(event.data)} ${getEndpointAssetTitle(event.data)}`,
          });
        }}
        suppressCellFocus
        domLayout="autoHeight"
      />
      {hasNextPage && (
        <Box display="flex" justifyContent="center">
          <Button
            size="sm"
            variant="outline"
            loading={isFetchingNextPage}
            onClick={() => void fetchNextPage()}
          >
            {t('loadMore')}
          </Button>
        </Box>
      )}
    </VStack>
  );
}
